/**
 * PII (Personally Identifiable Information) Sanitization for FEMA
 * Removes or masks sensitive data before error reporting
 */

// Type definitions
interface SanitizationOptions {
  maskChar?: string;
  preserveLength?: boolean;
  maskIPs?: boolean;
  [key: string]: any;
}

interface SanitizationStats {
  fieldsProcessed: number;
  fieldsRedacted: number;
  piiInstancesFound: number;
  bytesReduced: number;
}

// Common PII patterns
const PII_PATTERNS = {
  // Email addresses
  email: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
  
  // Phone Numbers (various formats)
  phone: /(\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})/g,
  
  // SSN (Social Security Number)
  ssn: /\b\d{3}-?\d{2}-?\d{4}\b/g,
  
  // Credit card numbers (basic pattern)
  creditCard: /\b(?:\d{4}[-\s]?){3}\d{4}\b/g,
  
  // IP addresses
  ipAddress: /\b(?:[0-9]{1,3}\.){3}[0-9]{1,3}\b/g,
  
  // API keys and tokens (common patterns)
  apiKey: /\b[A-Za-z0-9]{32,}\b/g,
  
  // JWT tokens
  jwt: /\beyJ[A-Za-z0-9_-]*\.eyJ[A-Za-z0-9_-]*\.[A-Za-z0-9_-]*\b/g,
  
  // URLs with potential sensitive info
  sensitiveUrl: /https?:\/\/[^\s]*(?:token|key|password|secret|auth)[^\s]*/gi,
  
  // Common sensitive field names in objects
  sensitiveFields: /(password|secret|token|key|auth|credential|pin|ssn|social)/i,
};

// Fields that commonly contain PII
const PII_FIELD_NAMES = [
  'password',
  'passwd',
  'pwd',
  'secret',
  'token',
  'auth',
  'authorization',
  'credential',
  'apikey',
  'api_key',
  'access_token',
  'refresh_token',
  'session_id',
  'sessionid',
  'ssn',
  'social_security_number',
  'credit_card',
  'creditcard',
  'card_number',
  'cardnumber',
  'cvv',
  'cvc',
  'pin',
  'phone',
  'telephone',
  'mobile',
  'email',
  'email_address',
  'first_name',
  'firstname',
  'last_name',
  'lastname',
  'full_name',
  'fullname',
  'address',
  'street',
  'city',
  'zip',
  'postal_code',
  'birthdate',
  'date_of_birth',
  'dob',
];

/**
 * Sanitize a string by replacing PII with masked values
 * @param {string} text - Text to sanitize
 * @param {SanitizationOptions} options - Sanitization options
 * @returns {string} Sanitized text
 */
export const sanitizeString = (text: string | any, options: SanitizationOptions = {}): string => {
  if (typeof text !== 'string') {
    return String(text);
  }
  
  const maskChar = options.maskChar || '*';
  const preserveLength = options.preserveLength !== false;
  
  let sanitized = text;
  
  // Replace email addresses
  sanitized = sanitized.replace(PII_PATTERNS.email, (match) => {
    if (preserveLength) {
      const parts = match.split('@');
      const local = parts[0];
      const domain = parts[1];
      
      if (!local || !domain) {
        return '[EMAIL]';
      }
      
      const maskedLocal = local.length > 2 
        ? local.charAt(0) + maskChar.repeat(Math.max(0, local.length - 2)) + local.charAt(local.length - 1)
        : maskChar.repeat(local.length);
      const maskedDomain = domain.length > 2
        ? domain.charAt(0) + maskChar.repeat(Math.max(0, domain.length - 2)) + domain.charAt(domain.length - 1)
        : maskChar.repeat(domain.length);
      return `${maskedLocal}@${maskedDomain}`;
    }
    return '[EMAIL]';
  });
  
  // Replace phone numbers
  sanitized = sanitized.replace(PII_PATTERNS.phone, () => {
    return preserveLength ? maskChar.repeat(10) : '[PHONE]';
  });
  
  // Replace SSN
  sanitized = sanitized.replace(PII_PATTERNS.ssn, () => {
    return preserveLength ? `${maskChar.repeat(3)}-${maskChar.repeat(2)}-${maskChar.repeat(4)}` : '[SSN]';
  });
  
  // Replace credit card numbers
  sanitized = sanitized.replace(PII_PATTERNS.creditCard, (match) => {
    if (preserveLength) {
      return maskChar.repeat(12) + match.slice(-4);
    }
    return '[CARD]';
  });
  
  // Replace IP addresses (optional - might be needed for debugging)
  if (options.maskIPs !== false) {
    sanitized = sanitized.replace(PII_PATTERNS.ipAddress, () => {
      return preserveLength ? `${maskChar.repeat(3)}.${maskChar.repeat(3)}.${maskChar.repeat(3)}.${maskChar.repeat(3)}` : '[IP]';
    });
  }
  
  // Replace API keys and long tokens
  sanitized = sanitized.replace(PII_PATTERNS.apiKey, (match) => {
    if (match.length >= 32) {
      return preserveLength ? maskChar.repeat(match.length) : '[API_KEY]';
    }
    return match; // Keep shorter strings
  });
  
  // Replace JWT tokens
  sanitized = sanitized.replace(PII_PATTERNS.jwt, () => '[JWT_TOKEN]');
  
  // Replace sensitive URLs
  sanitized = sanitized.replace(PII_PATTERNS.sensitiveUrl, () => '[SENSITIVE_URL]');
  
  return sanitized;
};

/**
 * Sanitize an object by recursively cleaning its properties
 * @param {any} obj - Object to sanitize
 * @param {SanitizationOptions} options - Sanitization options
 * @param {Set} visited - Set of visited objects (for circular reference detection)
 * @returns {any} Sanitized object
 */
export const sanitizeObject = (obj: any, options: SanitizationOptions = {}, visited: Set<any> = new Set()): any => {
  // Handle circular references
  if (visited.has(obj)) {
    return '[Circular Reference]';
  }
  
  // Handle null/undefined
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  // Handle primitives
  if (typeof obj !== 'object') {
    return typeof obj === 'string' ? sanitizeString(obj, options) : obj;
  }
  
  // Handle arrays
  if (Array.isArray(obj)) {
    visited.add(obj);
    const sanitized = obj.map(item => sanitizeObject(item, options, visited));
    visited.delete(obj);
    return sanitized;
  }
  
  // Handle Error objects
  if (obj instanceof Error) {
    return {
      name: obj.name,
      message: sanitizeString(obj.message, options),
      stack: sanitizeString(obj.stack || '', options),
    };
  }
  
  // Handle Date objects
  if (obj instanceof Date) {
    return obj.toISOString();
  }
  
  // Handle regular objects
  visited.add(obj);
  const sanitized: Record<string, unknown> = {};
  
  for (const [key, value] of Object.entries(obj)) {
    const lowerKey = key.toLowerCase();
    
    // Check if the key indicates PII
    const isPIIField = PII_FIELD_NAMES.some(piiField => 
      lowerKey.includes(piiField) || lowerKey === piiField
    );
    
    if (isPIIField) {
      // Mask the entire value for PII fields
      if (typeof value === 'string') {
        sanitized[key] = options.preserveLength !== false ? 
          '*'.repeat(Math.min(value.length, 20)) : '[REDACTED]';
      } else {
        sanitized[key] = '[REDACTED]';
      }
    } else {
      // Recursively sanitize non-PII fields
      sanitized[key] = sanitizeObject(value, options, visited);
    }
  }
  
  visited.delete(obj);
  return sanitized;
};

/**
 * Main sanitization function - automatically detects type and sanitizes
 * @param {any} data - Data to sanitize
 * @param {SanitizationOptions} options - Sanitization options
 * @returns {any} Sanitized data
 */
export const sanitizePII = (data: any, options: SanitizationOptions = {}): any => {
  try {
    return sanitizeObject(data, {
      maskChar: '*',
      preserveLength: true,
      maskIPs: false, // Keep IPs for debugging by default
      ...options,
    });
  } catch (error) {
    // If sanitization fails, return a safe fallback
    console.warn('PII sanitization failed:', error);
    return '[SANITIZATION_ERROR]';
  }
};

/**
 * Sanitize HTTP request/response data
 * @param {any} httpData - HTTP request/response data
 * @param {SanitizationOptions} options - Sanitization options
 * @returns {any} Sanitized HTTP data
 */
export const sanitizeHttpData = (httpData: any, options: SanitizationOptions = {}): any => {
  if (!httpData || typeof httpData !== 'object') {
    return httpData;
  }
  
  const sanitized = { ...httpData };
  
  // Sanitize headers
  if (sanitized.headers) {
    const sensitiveHeaders = [
      'authorization',
      'cookie',
      'set-cookie',
      'x-auth-token',
      'x-api-key',
      'authentication',
    ];
    
    sanitized.headers = { ...sanitized.headers };
    
    for (const [key, value] of Object.entries(sanitized.headers)) {
      if (sensitiveHeaders.some(header => key.toLowerCase().includes(header))) {
        sanitized.headers[key] = '[REDACTED]';
      } else if (typeof value === 'string') {
        sanitized.headers[key] = sanitizeString(value, options);
      }
    }
  }
  
  // Sanitize request/response body
  if (sanitized.data) {
    sanitized.data = sanitizeObject(sanitized.data, options);
  }
  
  if (sanitized.body) {
    sanitized.body = sanitizeObject(sanitized.body, options);
  }
  
  // Sanitize URL parameters
  if (sanitized.url) {
    sanitized.url = sanitizeString(sanitized.url, options);
  }
  
  return sanitized;
};

/**
 * Create a sanitized error context for FEMA reporting
 * @param {any} context - Error context
 * @param {SanitizationOptions} options - Sanitization options
 * @returns {any} Sanitized context
 */
export const sanitizeErrorContext = (context: any, options: SanitizationOptions = {}): any => {
  const sanitized = sanitizeObject(context, options);
  
  // Additional context-specific sanitization
  if (sanitized.user) {
    // Keep minimal user info for debugging, sanitize the rest
    const userInfo = { ...sanitized.user };
    delete userInfo.password;
    delete userInfo.token;
    delete userInfo.secret;
    
    // Keep only ID and non-PII fields
    sanitized.user = {
      id: userInfo.id,
      role: userInfo.role,
      // Remove any other potentially sensitive fields
    };
  }
  
  // Sanitize form data
  if (sanitized.formData) {
    sanitized.formData = sanitizeObject(sanitized.formData, options);
  }
  
  // Sanitize localStorage/sessionStorage data
  if (sanitized.localStorage) {
    sanitized.localStorage = sanitizeObject(sanitized.localStorage, options);
  }
  
  if (sanitized.sessionStorage) {
    sanitized.sessionStorage = sanitizeObject(sanitized.sessionStorage, options);
  }
  
  return sanitized;
};

/**
 * Test if a string contains PII
 * @param {string | any} text - Text to test
 * @returns {boolean} True if PII is detected
 */
export const containsPII = (text: string | any): boolean => {
  if (typeof text !== 'string') {
    return false;
  }
  
  return Object.values(PII_PATTERNS).some(pattern => pattern.test(text));
};

/**
 * Get sanitization statistics
 * @param {any} original - Original data
 * @param {any} sanitized - Sanitized data
 * @returns {SanitizationStats} Sanitization statistics
 */
export const getSanitizationStats = (original: any, sanitized: any): SanitizationStats => {
  const stats = {
    fieldsProcessed: 0,
    fieldsRedacted: 0,
    piiInstancesFound: 0,
    bytesReduced: 0,
  };
  
  try {
    const originalStr = JSON.stringify(original);
    const sanitizedStr = JSON.stringify(sanitized);
    
    stats.bytesReduced = originalStr.length - sanitizedStr.length;
    stats.piiInstancesFound = (originalStr.match(/\[REDACTED\]|\[EMAIL\]|\[PHONE\]|\[SSN\]|\[CARD\]/g) || []).length;
  } catch (_error) {
    // Ignore JSON serialization errors
  }
  
  return stats;
};

export default sanitizePII;
